// FIX-CCS-OUTBOX (spec §7.3) — the sync runner.
//
// One runner at a time across tabs (a lock record in IndexedDB with a short expiry, renewed as a heartbeat
// while the runner works — see LOCK_TTL_MS). Only the signed-in farmer's items are sent (owner
// binding, TR-OFF-9). Oldest first, one at a time.
//
// upload_source: 'live' for the photo just saved, on the run started by that save. Every other send is 'queued'
// (with client_queued_at) — EXCEPT when the offline-photo switch is OFF (Adeel, 30 Sep): then items already on the
// phone are sent as 'live', which the server accepts, so nothing waits out its 7 days and is lost. If the server
// still answers 403 offline_disabled (the phone's cached policy was stale), the same item is re-sent at once as
// 'live'. Offline evidence is kept either way. The token is read fresh from getSession() for every send.
//
// Outcomes (see sendItem.ts): ok → confirm the row exists by photo id → delete the item.
//   network / timeout / 5xx → keep, offline evidence, back off, stop this run (no signal: trying the next item is pointless)
//   401 → keep everything, stop, report auth_needed (R6: resume after the PIN)
//   403 offline_disabled → re-send the same item as 'live' in this run (switch-off rule); a 403 on a live send → keep, stop
//   other 4xx → failed state (Retry); carry on with the next item
// Expired items (R1) are marked 'expired' and reported; they are deleted only after the farmer has
// been told (acknowledgeExpired), so an app closed in between tells them again next time.

import { backoffMs, isExpired } from './limits';
import { OUTBOX_TEST_BREAKS_ON, testBreak } from './testBreaks';
import type { OutboxStore } from './outboxStore';
import type { SendOutcome } from './sendItem';
import type { OutboxItem } from './types';

export type RunTrigger = 'save' | 'online' | 'app_open' | 'screen_open' | 'send_now' | 'retry' | 'login';

export interface RunnerSession {
  userId: string;
  token: string;
}

export interface RunnerDeps {
  store: OutboxStore;
  getSession: () => RunnerSession | null;
  send: (item: OutboxItem, uploadSource: 'live' | 'queued', token: string, atSave: boolean) => Promise<SendOutcome>;
  /** true = the row exists for this farmer; false = it does not; null = could not check (network). */
  confirmRow: (photoId: string) => Promise<boolean | null>;
  now?: () => number;
  /** A per-tab id; the lock holder is `${holderId}`. */
  holderId: string;
  log?: (msg: string, data?: unknown) => void;
  /** The farmer's offline-photo switch as last read (OFF if never read). OFF → queued items go as 'live'. */
  policyEnabled?: () => boolean;
  /**
   * FIX-CCS-G-GPS: photo ids the location engine in THIS page is still holding. A 'holding' item that is not in it
   * (the app was closed or reloaded during the hold) can never resolve, so it is finalised with no evidence fix.
   * Absent → no holds are live.
   */
  liveHolds?: () => ReadonlySet<string>;
}

export type RunStop = 'done' | 'no_session' | 'busy' | 'network' | 'auth_needed' | 'offline_disabled' | 'not_confirmed' | 'guard';

export interface RunResult {
  stop: RunStop;
  sent: string[];
  duplicates: string[];
  failed: string[];
  /** Mine, newly marked expired in this run (the farmer must be told). */
  expired: number;
  attempted: number;
  /** The server's reply for each photo sent in this run (the camera uses it for the photo just saved). */
  bodies: Record<string, Record<string, unknown>>;
}

// The lock is a heartbeat: it lives 20 s and the holder renews it every 8 s while it works (also during a long
// send). A tab that is closed mid-run therefore blocks the others for at most ~20 s, never for a whole send limit.
export const LOCK_TTL_MS = 20_000;
export const LOCK_RENEW_MS = 8_000;

export interface RunOptions {
  trigger: RunTrigger;
  /** The photo just saved (its first attempt is the live send). */
  savedPhotoId?: string;
  /** Ignore the back-off wait (the farmer tapped "Send now" or Retry). */
  force?: boolean;
}

export async function runOutbox(deps: RunnerDeps, opts: RunOptions): Promise<RunResult> {
  const now = deps.now ?? Date.now;
  const log = deps.log ?? (() => {});
  const result: RunResult = { stop: 'done', sent: [], duplicates: [], failed: [], expired: 0, attempted: 0, bodies: {} };

  const session = deps.getSession();
  if (!session) return { ...result, stop: 'no_session' };

  if (!(await deps.store.acquireLock(deps.holderId, LOCK_TTL_MS, now()))) {
    log('runner busy (another tab holds the lock)');
    return { ...result, stop: 'busy' };
  }

  try {
    // A 'sending' item seen while WE hold the lock was left by a runner that died mid-send → pending again.
    const all = await deps.store.all();
    for (const it of all) {
      if (it.owner_user_id !== session.userId) continue;
      if (it.state === 'sending') await deps.store.update(it.photo_id, { state: 'pending' });
      if (it.location_state === 'holding' && !(deps.liveHolds?.().has(it.photo_id) ?? false)) {
        await deps.store.update(it.photo_id, { location_state: 'final', location_bundle: null });
        log('hold lost (app closed during the hold) → final without a fix', it.photo_id);
      }
      if ((it.state === 'pending' || it.state === 'sending' || it.state === 'failed') && isExpired(it, now())) {
        await deps.store.update(it.photo_id, { state: 'expired' });
        result.expired++;
      }
    }

    let guard = all.length + 5;
    let savedLiveUsed = false;
    // Items the server refused as queued in this run (403 offline_disabled): re-sent as live.
    const forceLive = new Set<string>();
    while (guard-- > 0) {
      // The session may have changed between sends (logout / another farmer): re-read it every time.
      const cur = deps.getSession();
      if (!cur || cur.userId !== session.userId) return { ...result, stop: 'no_session' };

      const items = (await deps.store.all())
        .filter((i) => ((OUTBOX_TEST_BREAKS_ON && testBreak('owner')) || i.owner_user_id === session.userId) && i.state === 'pending')
        .filter((i) => i.location_state !== 'holding') // GPS Fix §5.3: never sent as evidence while the hold runs
        .filter((i) => opts.force || i.photo_id === opts.savedPhotoId || !i.next_attempt_at || Date.parse(i.next_attempt_at) <= now())
        .sort((a, b) => a.queued_at.localeCompare(b.queued_at) || a.claims.client_seq - b.claims.client_seq);
      const item = items[0];
      if (!item) return result;

      const live = opts.trigger === 'save' && item.photo_id === opts.savedPhotoId && !item.save_attempt_done && !savedLiveUsed;
      if (live) savedLiveUsed = true;
      const switchOff = deps.policyEnabled ? !deps.policyEnabled() : false;
      const uploadSource: 'live' | 'queued' = live || switchOff || forceLive.has(item.photo_id) ? 'live' : 'queued';

      await deps.store.acquireLock(deps.holderId, LOCK_TTL_MS, now());
      await deps.store.update(item.photo_id, {
        state: 'sending',
        attempts: item.attempts + 1,
        last_attempt_at: new Date(now()).toISOString(),
        save_attempt_done: true,
      });
      result.attempted++;
      log(`send ${item.photo_id} as ${uploadSource} (attempt ${item.attempts + 1})`);

      const heartbeat = setInterval(() => {
        deps.store.acquireLock(deps.holderId, LOCK_TTL_MS, now()).catch(() => undefined);
      }, LOCK_RENEW_MS);
      let outcome: SendOutcome;
      try {
        outcome = await deps.send({ ...item, attempts: item.attempts + 1 }, uploadSource, cur.token, live);
      } finally {
        clearInterval(heartbeat);
      }
      log(`outcome ${item.photo_id}: ${outcome.kind}`, outcome);

      if (outcome.kind === 'ok') {
        const exists = await deps.confirmRow(item.photo_id);
        if (exists === true) {
          await deps.store.delete(item.photo_id);
          (outcome.duplicate ? result.duplicates : result.sent).push(item.photo_id);
          result.bodies[item.photo_id] = outcome.body;
          continue;
        }
        // The server said yes but the row cannot be seen: keep the item; the next send gets duplicate:true.
        await deps.store.update(item.photo_id, {
          state: 'pending',
          last_error: 'row_not_confirmed',
          last_error_detail: exists === null ? 'confirm read failed' : 'row not found',
          next_attempt_at: new Date(now() + backoffMs(item.attempts + 1)).toISOString(),
        });
        return { ...result, stop: 'not_confirmed' };
      }

      if (outcome.kind === 'network' || outcome.kind === 'timeout' || outcome.kind === 'server_error') {
        await deps.store.update(item.photo_id, {
          state: 'pending',
          send_failed_before: true,
          last_error: outcome.kind === 'server_error' ? 'server_error' : outcome.kind,
          last_error_detail: outcome.detail,
          next_attempt_at: new Date(now() + backoffMs(item.attempts + 1)).toISOString(),
        });
        return { ...result, stop: 'network' };
      }

      if (outcome.kind === 'auth') {
        await deps.store.update(item.photo_id, {
          state: 'pending',
          send_failed_before: true,
          last_error: 'session_invalid',
          last_error_detail: outcome.detail,
          next_attempt_at: null,
        });
        return { ...result, stop: 'auth_needed' };
      }

      if (outcome.kind === 'offline_disabled' && uploadSource === 'queued' && !forceLive.has(item.photo_id)) {
        // Switch-off rule: the server refuses queued uploads while the switch is off → send it as live now.
        forceLive.add(item.photo_id);
        await deps.store.update(item.photo_id, { state: 'pending', send_failed_before: true, last_error: 'offline_disabled', last_error_detail: 'resending as live', next_attempt_at: null });
        continue;
      }

      if (outcome.kind === 'offline_disabled') {
        await deps.store.update(item.photo_id, {
          state: 'pending',
          send_failed_before: true,
          last_error: 'offline_disabled',
          last_error_detail: outcome.detail,
          next_attempt_at: null,
        });
        return { ...result, stop: 'offline_disabled' };
      }

      // refused (other 4xx): failed with Retry; never dropped. Carry on with the rest.
      await deps.store.update(item.photo_id, {
        state: 'failed',
        send_failed_before: true,
        last_error: 'refused',
        last_error_detail: `${outcome.status} ${outcome.detail}`,
        next_attempt_at: null,
      });
      result.failed.push(item.photo_id);
    }
    return { ...result, stop: 'guard' };
  } finally {
    await deps.store.releaseLock(deps.holderId);
  }
}

/** Retry one failed item: back to pending with no wait. */
export async function retryItem(store: OutboxStore, photoId: string, userId: string): Promise<boolean> {
  const it = await store.get(photoId);
  if (!it || it.owner_user_id !== userId || it.state !== 'failed') return false;
  await store.update(photoId, { state: 'pending', next_attempt_at: null });
  return true;
}

/** The farmer has been told about the expired items: remove them (theirs only). Returns how many were removed. */
export async function acknowledgeExpired(store: OutboxStore, userId: string): Promise<number> {
  const items = await store.all();
  let n = 0;
  for (const it of items) {
    if (it.owner_user_id === userId && it.state === 'expired') {
      await store.delete(it.photo_id);
      n++;
    }
  }
  return n;
}
